import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { SharedBookingServices } from '@views/common/models';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { ItineraryTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/composed/basket';
import {
  AccordionItemComponent,
  AccordionTypeEnum,
  AccordionVariantEnum
} from '@vueling-ng/vy-ds/src/lib/components/globals/accordion';
import { IconTextSizeEnum, IconTextTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/icon-text';
import { AccordionSection } from '../../models/accordion-section.model';
import {
  ContactInformation,
  FlightJourney,
  PaymentDetails,
  ServicesPerPassenger
} from '../../models/booking-details.model';

@Component({
  selector: 'app-pax-services-and-contact-information',
  templateUrl: './pax-services-and-contact-information.component.html',
  styleUrl: './pax-services-and-contact-information.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaxServicesAndContactInformationComponent {
  @ViewChild('paxServices', { read: ElementRef }) private readonly paxServicesDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('paxServices', { static: true }) private readonly paxServicesComponent!: AccordionItemComponent;

  @ViewChild('otherServices', { read: ElementRef }) private readonly otherServicesDiv?: ElementRef<HTMLDivElement>;
  @ViewChild('otherServices', { static: false }) private readonly otherServicesComponent?: AccordionItemComponent;

  @ViewChild('agencyContact', { read: ElementRef }) private readonly agencyContactDiv?: ElementRef<HTMLDivElement>;
  @ViewChild('agencyContact', { static: false }) private readonly agencyContactComponent?: AccordionItemComponent;

  @ViewChild('paxContact', { read: ElementRef }) private readonly paxContactDiv?: ElementRef<HTMLDivElement>;
  @ViewChild('paxContact', { static: false }) private readonly paxContactComponent?: AccordionItemComponent;

  @ViewChild('payment', { read: ElementRef }) private readonly paymentDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('payment', { static: true }) private readonly paymentComponent!: AccordionItemComponent;

  @Input({ required: true })
  public sharedBookingServices!: SharedBookingServices[];
  @Input({ required: true })
  public passengerServices!: ServicesPerPassenger[] | undefined;
  @Input({ required: true })
  public paymentDetails!: PaymentDetails;
  @Input({ required: true })
  public contactInformationAgency!: ContactInformation;
  @Input({ required: true })
  public contactInformationPrimary!: ContactInformation;
  @Input({ required: true })
  public currencyCode: string | undefined;
  @Input({ required: true })
  public recordLocator: string | undefined;
  @Input({ required: true })
  public itineraryType!: ItineraryTypeEnum;
  @Input({ required: true })
  public stations!: Station[];
  @Input({ required: true })
  public flightJourneys!: FlightJourney[];

  public readonly accordionType: AccordionTypeEnum = AccordionTypeEnum.MULTIPLE;
  public readonly accordionVariant = AccordionVariantEnum;
  public readonly iconType: IconTextTypeEnum = IconTextTypeEnum.HEADING_M;
  public readonly iconSize: IconTextSizeEnum = IconTextSizeEnum.M;

  constructor() {
    if (this.passengerServices?.every((ps) => !ps.outboundServices)) {
      this.passengerServices = undefined;
    }
  }

  private getAccordionSections(): AccordionSection[] {
    return [
      { component: this.paxServicesComponent, element: this.paxServicesDiv },
      { component: this.otherServicesComponent, element: this.otherServicesDiv },
      { component: this.agencyContactComponent, element: this.agencyContactDiv },
      { component: this.paxContactComponent, element: this.paxContactDiv },
      { component: this.paymentComponent, element: this.paymentDiv }
    ];
  }

  public openSections(): void {
    this.getAccordionSections().forEach((section) => {
      if (!section.component?.active) {
        section.element?.nativeElement.querySelector('button')?.click();
      }
    });
  }

  public getSections(withPrice: boolean): ElementRef<HTMLDivElement>[] {
    const sections = [];
    if (this.otherServicesDiv) {
      sections.push(this.otherServicesDiv);
    }
    if (this.agencyContactDiv) {
      sections.push(this.agencyContactDiv);
    }
    if (this.paxContactDiv) {
      sections.push(this.paxContactDiv);
    }

    sections.push(this.paxServicesDiv);

    if (withPrice) {
      sections.push(this.paymentDiv);
    }

    return sections;
  }
}
